import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import {
  Dumbbell, BarChart3, Lock, Loader2, Trophy, PartyPopper, X, TrendingUp, ChevronLeft, Crown, Sparkles, BookOpen, Flame, Archive, Trash2, RotateCcw, Share2,
} from 'lucide-react';
import { toast } from 'sonner';
import { shareRecordImage } from '../shareRecord';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { useTourRegister } from '../../../utils/tourNav';
import { ProgramOverview } from './ProgramOverview';
import { SessionRunner } from './SessionRunner';
import { WorkoutStats } from './WorkoutStats';
import { RecordsHall } from './RecordsHall';
import { Confetti } from './RestTimer';
import { SmartTooltip } from '../../../components/SmartTooltip';
import { NewProgramModal } from './NewProgramModal';
import { MyProgramBuilder } from './MyProgramBuilder';
import { AssistedProgramBuilder } from './AssistedProgramBuilder';
import { ClientAIWorkoutGenerator } from './ClientAIWorkoutGenerator';
import ExerciseLibrary from './ExerciseLibrary';
import { CustomExerciseLibrary } from './CustomExerciseLibrary';
import { CardioHub } from './CardioHub';
import { MethodologyGuide } from './MethodologyGuide';
import { ProgramCardMenu } from './ProgramCardMenu';
import { StandardProgramsPicker } from './StandardProgramsPicker';
import { AppMenuTile } from '../../../components/AppMenuTile';

const ProgramsList = ({ en, authFetch, onBack, onOpen, onNewProgram, onGuide }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [statusTab, setStatusTab] = useState('active');
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPrograms = useCallback((status = statusTab) => {
    setLoading(true);
    const url = status === 'trash' ? '/api/tracking/programs-pro/trash/list' : `/api/tracking/programs-pro?status=${status}`;
    authFetch(url).then((r) => r.json()).then((d) => {
      setPrograms(d.programs || []);
    }).finally(() => setLoading(false));
  }, [authFetch, statusTab]);
  useEffect(() => { loadPrograms(statusTab); }, [statusTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const duplicateProgram = (id) => authFetch(`/api/tracking/programs-pro/${id}/duplicate`, { method: 'POST' }).then((r) => { if (!r.ok) throw new Error(); loadPrograms(); });
  const toggleArchiveProgram = (id, archived) => authFetch(`/api/tracking/programs-pro/${id}/${archived ? 'unarchive' : 'archive'}`, { method: 'POST' }).then((r) => { if (!r.ok) throw new Error(); loadPrograms(); });
  const deleteProgram = (id) => authFetch(`/api/tracking/programs-pro/${id}`, { method: 'DELETE' }).then((r) => {
    if (!r.ok) throw new Error('delete_failed');
    loadPrograms();
  });
  const restoreProgram = (id) => authFetch(`/api/tracking/programs-pro/${id}/restore`, { method: 'POST' }).then((r) => {
    if (!r.ok) throw new Error('restore_failed');
    toast.success(L('Programme restauré !', 'Program restored!'));
    loadPrograms();
  }).catch(() => toast.error(L('Restauration impossible (délai de 24h dépassé ?).', 'Could not restore (past the 24h window?).')));
  const shareProgram = async (id) => {
    const r = await authFetch(`/api/tracking/programs-pro/${id}/share`, { method: 'POST' });
    const d = await r.json();
    const url = `${window.location.origin}/programme-partage/${d.share_token}`;
    if (navigator.share) {
      await navigator.share({ title: 'Body-Up — Programme musculation', url }).catch(() => {});
    } else {
      await navigator.clipboard.writeText(url);
      toast.success(L('Lien copié dans le presse-papiers !', 'Link copied to clipboard!'));
    }
  };

  return (
    <div className="space-y-4" data-testid="my-workouts-list">
      {onBack && (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="back-to-hub-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-black text-white flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-primary" /> {L('Mes entraînements', 'My workouts')}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onGuide} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/10 text-zinc-300 text-xs font-semibold hover:border-primary/40" data-testid="methodology-guide-btn">
            <BookOpen className="w-3.5 h-3.5" /> {L('Guide', 'Guide')}
          </button>
          <button
            onClick={onNewProgram}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform"
            data-testid="new-program-btn"
          >
            <Sparkles className="w-4 h-4" /> {L('Nouveau programme', 'New program')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {[{ v: 'active', label: L('Actifs', 'Active') }, { v: 'archived', label: L('Archivés', 'Archived') }, { v: 'trash', label: L('Corbeille', 'Trash') }].map((t) => (
          <button key={t.v} onClick={() => setStatusTab(t.v)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${statusTab === t.v ? 'bg-white/15 text-white' : 'bg-transparent text-zinc-500 hover:text-zinc-300'}`}
            data-testid={`musculation-status-tab-${t.v}`}>
            {t.v === 'archived' && <Archive className="w-3 h-3 inline mr-1" />}{t.v === 'trash' && <Trash2 className="w-3 h-3 inline mr-1" />}{t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : programs.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-8 text-center text-zinc-500 text-sm" data-testid="no-programs-message">
          {statusTab === 'archived'
            ? L('Aucun programme archivé.', 'No archived program.')
            : statusTab === 'trash'
            ? L('Corbeille vide.', 'Trash is empty.')
            : L('Aucun programme assigné pour le moment. Votre coach vous en attribuera un prochainement.', 'No program assigned yet. Your coach will assign one soon.')}
        </div>
      ) : statusTab === 'trash' ? programs.map((p) => {
        const hoursLeft = Math.max(0, Math.ceil(24 - (Date.now() - new Date(p.deleted_at).getTime()) / 3600000));
        return (
          <div key={p.program_id} className="w-full rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-5" data-testid={`trash-program-card-${p.program_id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white font-bold text-lg truncate">{p.name}</p>
                <p className="text-xs text-zinc-500">{L(`Supprimé — récupérable encore ${hoursLeft}h`, `Deleted — recoverable for ${hoursLeft}h more`)}</p>
              </div>
              <button onClick={() => restoreProgram(p.program_id)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-primary/20 border border-primary/40 hover:bg-primary/30 transition-colors" data-testid={`trash-restore-btn-${p.program_id}`}>
                <RotateCcw className="w-3.5 h-3.5" /> {L('Restaurer', 'Restore')}
              </button>
            </div>
          </div>
        );
      }) : programs.map((p) => {
        const pct = Math.min(100, Math.round((p.total_done / Math.max(1, p.total_target)) * 100));
        return (
        <div key={p.program_id} onClick={() => onOpen(p.program_id)} role="button" className="w-full text-left rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] hover:border-primary/40 p-5 transition-colors cursor-pointer" data-testid={`program-card-${p.program_id}`}>
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-white font-bold text-lg truncate">{p.name}</p>
              <p className="text-sm text-zinc-500 truncate">{p.goal}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className="text-xs font-black px-2.5 py-1 rounded-full bg-gradient-to-r from-primary/20 to-pink-500/20 text-primary border border-primary/30"
                data-testid={`program-progress-badge-${p.program_id}`}
              >
                {pct}%
              </span>
              <ProgramCardMenu en={en} isArchived={statusTab === 'archived'} testIdPrefix={`musculation-program-${p.program_id}`}
                onDuplicate={() => duplicateProgram(p.program_id)}
                onToggleArchive={() => toggleArchiveProgram(p.program_id, statusTab === 'archived')}
                onShare={() => shareProgram(p.program_id)}
                onDelete={() => deleteProgram(p.program_id)} />
            </div>
          </div>
          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-3">
            <div className="h-full bg-gradient-to-r from-primary to-pink-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-zinc-500 mt-1.5">{p.total_done}/{p.total_target} {L('séances complétées', 'sessions completed')}</p>
        </div>
        );
      })}
    </div>

  );
};

/** Célébration de fin de PROGRAMME ENTIER (dernière séance validée = objectif total atteint) —
 * moment fort volontairement plus spectaculaire que la célébration de fin de séance : confettis
 * prolongés, récap des stats de tout le programme, puis invitation à repartir sur un nouveau
 * cycle (soi-même ou via le crédit mensuel "programme personnalisé" déjà inclus au coaching). */
const ProgramCompletionModal = ({ completion, onClose, onCreateOwn, onRequestCoach, L }) => {
  const [showConfetti, setShowConfetti] = useState(false);
  useEffect(() => {
    if (completion) {
      setShowConfetti(true);
      const t = setTimeout(() => setShowConfetti(false), 4200);
      return () => clearTimeout(t);
    }
  }, [completion]);
  if (!completion) return null;
  const stats = [
    { label: L('Séances complétées', 'Sessions completed'), value: completion.sessions_completed, suffix: '' },
    { label: L('Tonnage total soulevé', 'Total tonnage lifted'), value: completion.total_tonnage, suffix: ' kg' },
    { label: L('Nouveaux records', 'New records'), value: completion.total_records, suffix: '' },
    { label: L('Durée du programme', 'Program duration'), value: completion.weeks_elapsed, suffix: completion.weeks_elapsed ? (L(' sem.', ' wks')) : '' },
  ].filter((s) => s.value != null);
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[106] bg-black/90 backdrop-blur-md flex items-center justify-center p-4" data-testid="program-completion-modal">
        {showConfetti && <Confetti />}
        <motion.div initial={{ scale: 0.7, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className="relative bg-gradient-to-br from-amber-500/10 via-zinc-900 to-black border border-amber-500/30 rounded-3xl p-7 max-w-md w-full text-center max-h-[92vh] overflow-y-auto shadow-[0_0_60px_-10px_rgba(245,158,11,0.35)]">
          <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="close-program-completion-btn"><X className="w-5 h-5" /></button>

          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center mx-auto mb-4 shadow-[0_0_40px_-4px_rgba(245,158,11,0.8)]">
            <Trophy className="w-10 h-10 text-white" />
          </motion.div>
          <h3 className="text-3xl font-black bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 bg-clip-text text-transparent mb-1" data-testid="program-completion-title">
            {L('PROGRAMME TERMINÉ !', 'PROGRAM COMPLETE!')}
          </h3>
          <p className="text-zinc-400 text-sm mb-6">
            {L(`Félicitations, vous avez terminé « ${completion.program_name} » du début à la fin. Quelle performance !`, `Congrats, you completed "${completion.program_name}" from start to finish. What a performance!`)}
          </p>

          <div className="grid grid-cols-2 gap-2.5 mb-6" data-testid="program-completion-stats">
            {stats.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.08 }}
                className="bg-black/40 border border-amber-500/15 rounded-xl p-3.5" data-testid={`program-completion-stat-${i}`}>
                <p className="text-2xl font-black text-amber-300">{s.value}{s.suffix}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wide">{s.label}</p>
              </motion.div>
            ))}
          </div>

          <div className="space-y-2.5">
            <p className="text-xs font-bold text-white uppercase tracking-wide">{L('Et maintenant ?', "What's next?")}</p>
            <button onClick={onRequestCoach}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl font-black text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:scale-[1.02] transition-transform shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)]"
              data-testid="program-completion-request-coach-btn">
              <Crown className="w-4 h-4" /> {L('Un programme personnalisé par mon coach', 'A personalized program by my coach')}
            </button>
            <p className="text-[11px] text-zinc-500 -mt-1">{L('Utilise ton crédit mensuel inclus · livré en 48h max', 'Uses your included monthly credit · delivered within 48h')}</p>
            <button onClick={onCreateOwn}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
              data-testid="program-completion-create-own-btn">
              <Sparkles className="w-4 h-4" /> {L('Créer mon prochain programme moi-même', 'Create my next program myself')}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const CelebrationModal = ({ celebration, onClose, L, en }) => {
  const hasRecords = celebration?.new_records?.length > 0;
  const [sharingIdx, setSharingIdx] = useState(null);
  const handleShareRecord = async (r, i) => {
    setSharingIdx(i);
    try {
      await shareRecordImage({ exerciseName: r.name, oldWeight: r.old_weight, newWeight: r.new_weight, pctChange: r.pct_change, en });
    } catch (e) { if (e?.name !== 'AbortError') toast.error(L('Impossible de générer l\u2019image', 'Could not generate the image')); }
    setSharingIdx(null);
  };
  return (
    <AnimatePresence>
      {celebration && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[105] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="celebration-modal">
          {(celebration.target_met || hasRecords) && <Confetti />}
          <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gradient-to-br from-zinc-900 to-black border border-primary/30 rounded-3xl p-8 max-w-sm text-center relative max-h-[85vh] overflow-y-auto">
            <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="close-celebration-btn"><X className="w-5 h-5" /></button>
            {hasRecords ? (
              <>
                <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-2xl font-black text-white mb-2">{L('NOUVEAU RECORD !', 'NEW RECORD!')}</h3>
                <div className="space-y-3 mb-2" data-testid="new-records-list">
                  {celebration.new_records.map((r, i) => (
                    <div key={i} className="bg-gradient-to-br from-yellow-500/10 to-primary/10 border border-yellow-500/25 rounded-xl p-3.5 text-left" data-testid={`new-record-item-${i}`}>
                      <p className="text-sm text-white font-bold mb-1">{r.name}</p>
                      <p className="text-xs text-zinc-400">
                        {L('Bravo ! Votre 1RM estimée est passée de', 'Congrats! Your estimated 1RM went from')} <span className="text-zinc-300 font-semibold">{r.old_weight}kg</span> {L('à', 'to')} <span className="text-yellow-400 font-bold">{r.new_weight}kg</span>
                      </p>
                      <p className="text-lg font-black text-yellow-400 mt-1">+{r.pct_change}%</p>
                      <button onClick={() => handleShareRecord(r, i)} disabled={sharingIdx === i}
                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-amber-300 hover:text-white disabled:opacity-50"
                        data-testid={`share-record-btn-${i}`}>
                        {sharingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Share2 className="w-3 h-3" />} {L('Partager', 'Share')}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            ) : celebration.target_met ? (
              <>
                <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-2xl font-black text-white mb-2">{L('OBJECTIF ATTEINT !', 'TARGET REACHED!')}</h3>
                <p className="text-zinc-400 text-sm mb-4">{L('Vous avez battu votre record de tonnage', 'You beat your tonnage record')}</p>
              </>
            ) : (
              <>
                <PartyPopper className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-black text-white mb-2">{L('Séance terminée !', 'Session complete!')}</h3>
                <p className="text-zinc-400 text-sm mb-4">{L('Continuez comme ça, le prochain objectif est proche.', 'Keep it up, the next target is within reach.')}</p>
              </>
            )}
            <div className="bg-black/40 rounded-xl p-4 space-y-1 mt-3">
              <p className="text-3xl font-black text-primary">{celebration.session_tonnage} kg</p>
              <p className="text-xs text-zinc-500">{L('Tonnage total de la séance', 'Total session tonnage')}</p>
              {celebration.tonnage_target && (
                <p className="text-xs text-zinc-600">{L('Objectif était', 'Target was')} {celebration.tonnage_target} kg</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/** Espace "Entraînements" ultra pro : hub d'icônes -> programmes -> séances -> stats. */
export default function WorkoutsHub({ en, initialProgramId, initialSessionLabel, onConsumeInitialProgramId }) {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [view, setView] = useState('hub'); // hub | programs-hub | programs | cardio | stats-hub | stats | records | overview | session | builder | ai-generate | assisted | library
  const [programsSubTab, setProgramsSubTab] = useState('musculation');
  const [statsSubTab, setStatsSubTab] = useState('stats');
  const [activeProgram, setActiveProgram] = useState(null);
  const [sessionCtx, setSessionCtx] = useState(null); // { session, occurrenceNumber, editingLog }
  const [celebration, setCelebration] = useState(null);
  const [programCompletion, setProgramCompletion] = useState(null);
  const [newProgramOpen, setNewProgramOpen] = useState(false);
  const [contextProgramName, setContextProgramName] = useState(null);
  const statsTileRef = useRef(null);
  useTourRegister('setWorkoutsView', setView);
  useTourRegister('setProgramsSubTab', setProgramsSubTab);
  useTourRegister('setStatsSubTab', setStatsSubTab);

  // Retour de Stripe (achat crédit IA supplémentaire, ou crédit bibliothèque perso) — rouvre l'écran concerné.
  useEffect(() => {
    if (searchParams.get('ai_credit_session')) setView('ai-generate');
    if (searchParams.get('custom_ex_credit_session')) setView('custom-library');
  }, [searchParams]);

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/tracking/programs-pro');
      const data = await res.json();
      setLocked(!!data.locked);
      setPrograms(data.programs || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [authFetch]);
  useEffect(() => { loadPrograms(); }, [loadPrograms]);

  const openProgram = useCallback(async (programId, sessionLabelToStart) => {
    try {
      const res = await authFetch(`/api/tracking/programs-pro/${programId}`);
      if (!res.ok) return;
      const data = await res.json();
      setActiveProgram(data);
      const sessionToStart = sessionLabelToStart && (data.sessions || []).find((s) => s.session_label === sessionLabelToStart);
      if (sessionToStart) { startSession(sessionToStart); return; }
      setView('overview');
    } catch { /* noop */ }
  }, [authFetch]);

  useEffect(() => {
    if (initialProgramId) {
      openProgram(initialProgramId, initialSessionLabel);
      onConsumeInitialProgramId && onConsumeInitialProgramId();
    }
  }, [initialProgramId]);

  const openProgramsList = () => {
    setProgramsSubTab('musculation');
    setView('programs-hub');
  };

  const closeNewProgramModal = () => { setNewProgramOpen(false); setContextProgramName(null); };
  const openAdjustmentRequest = (programName) => { setContextProgramName(programName); setNewProgramOpen(true); };
  const startNewProgramFromCompletion = () => { setProgramCompletion(null); setContextProgramName(null); setNewProgramOpen(true); setView('programs-hub'); };
  const requestCoachFromCompletion = () => { const name = programCompletion?.program_name || null; setProgramCompletion(null); openAdjustmentRequest(name); setView('programs-hub'); };

  const startSession = (session) => {
    setSessionCtx({ session, occurrenceNumber: session.done_count + 1, editingLog: null });
    setView('session');
  };

  const editLog = (session, log) => {
    setSessionCtx({ session, occurrenceNumber: log.occurrence_number, editingLog: log });
    setView('session');
  };

  const submitSession = async (exercises) => {
    const { session, editingLog } = sessionCtx;
    try {
      if (editingLog) {
        const res = await authFetch(`/api/tracking/programs-pro/${activeProgram.program_id}/log/${editingLog.log_id}`, {
          method: 'PUT', body: JSON.stringify({ session_label: session.session_label, exercises }),
        });
        if (!res.ok) throw new Error('failed');
        toast.success(L('Séance corrigée.', 'Session fixed.'));
      } else {
        const res = await authFetch(`/api/tracking/programs-pro/${activeProgram.program_id}/log`, {
          method: 'POST', body: JSON.stringify({ session_label: session.session_label, exercises }),
        });
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (data.program_complete) setProgramCompletion(data.program_complete);
        else setCelebration(data.log);
      }
      await openProgram(activeProgram.program_id);
      loadPrograms();
      setSessionCtx(null);
      setView('overview');
      return true;
    } catch {
      toast.error(L("Erreur lors de l'enregistrement.", 'Error saving your session.'));
      return false;
    }
  };

  const updateExerciseYoutube = async (exerciseId, url) => {
    try {
      await authFetch(`/api/tracking/programs-pro/${activeProgram.program_id}/exercise/${exerciseId}/youtube`, {
        method: 'PATCH', body: JSON.stringify({ youtube_url: url }),
      });
      toast.success(L('Lien vidéo mis à jour.', 'Video link updated.'));
      const res = await authFetch(`/api/tracking/programs-pro/${activeProgram.program_id}`);
      const updated = await res.json();
      setActiveProgram(updated);
      setSessionCtx((prev) => prev ? { ...prev, session: updated.sessions.find((s) => s.session_label === prev.session.session_label) || prev.session } : prev);
    } catch { toast.error(L('Erreur.', 'Error.')); }
  };

  const updateExerciseRestTime = async (exerciseId, restSeconds) => {
    const res = await authFetch(`/api/tracking/programs-pro/${activeProgram.program_id}/exercise/${exerciseId}/rest-seconds`, {
      method: 'PATCH', body: JSON.stringify({ rest_seconds: restSeconds }),
    });
    if (!res.ok) throw new Error('failed');
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  if (locked) {
    return (
      <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-8 text-center" data-testid="workouts-locked">
        <Lock className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-white font-bold text-lg mb-2">{L('Débloquez le module Musculation', 'Unlock the Workout module')}</h3>
        <p className="text-zinc-500 text-sm mb-5">{L('Passez à Suivi Pro+ (19.90 CHF/mois, essai 7 jours gratuit) pour créer vos programmes vous-même, ou à la Communauté (49 CHF/mois) pour un programme fait par votre coach.', 'Upgrade to Suivi Pro+ (CHF 19.90/month, 7-day free trial) to build your own programs, or to Communauté (CHF 49/month) for a coach-made program.')}</p>
        <a href="/mon-espace?tab=profil&plan=solo" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500" data-testid="workouts-locked-cta">
          {L('Débloquer mon accès', 'Unlock my access')}
        </a>
      </div>
    );
  }

  if (view === 'session' && sessionCtx) {
    return (
      <>
        <SessionRunner
          en={en} session={sessionCtx.session} occurrenceNumber={sessionCtx.occurrenceNumber} editingLog={sessionCtx.editingLog}
          onBack={() => setView('overview')} onSubmit={submitSession} onExerciseYoutubeUpdate={updateExerciseYoutube}
          onRestTimeGlobalChange={updateExerciseRestTime} authFetch={authFetch} programId={activeProgram?.program_id}
        />
        <CelebrationModal celebration={celebration} onClose={() => setCelebration(null)} L={L} en={en} />
      </>
    );
  }

  if (view === 'overview' && activeProgram) {
    return (
      <>
        <ProgramOverview program={activeProgram} en={en} onBack={() => setView('programs-hub')} onStartSession={startSession} onEditLog={editLog} authFetch={authFetch} />
        <CelebrationModal celebration={celebration} onClose={() => setCelebration(null)} L={L} en={en} />
        <ProgramCompletionModal completion={programCompletion} onClose={() => setProgramCompletion(null)}
          onCreateOwn={startNewProgramFromCompletion} onRequestCoach={requestCoachFromCompletion} L={L} />
      </>
    );
  }

  if (view === 'methodology-guide') {
    return <MethodologyGuide en={en} onBack={() => setView('programs-hub')} />;
  }

  if (view === 'programs-hub') {
    return (
      <div className="space-y-4" data-testid="programs-hub">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setView('hub')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="programs-hub-back-btn">
            <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
          </button>
          <div className="inline-flex rounded-xl bg-zinc-900 border border-white/10 p-1 gap-1">
            <button onClick={() => setProgramsSubTab('musculation')} data-testid="programs-hub-tab-musculation"
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition-colors ${programsSubTab === 'musculation' ? 'bg-gradient-to-r from-purple-500 to-primary text-white' : 'text-zinc-400 hover:text-white'}`}>
              <Dumbbell className="w-4 h-4" /> {L('Programmes musculation', 'Strength programs')}
            </button>
            <button onClick={() => setProgramsSubTab('cardio')} data-testid="programs-hub-tab-cardio"
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition-colors ${programsSubTab === 'cardio' ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white' : 'text-zinc-400 hover:text-white'}`}>
              <Flame className="w-4 h-4" /> {L('Programmes cardio', 'Cardio programs')}
            </button>
          </div>
        </div>
        {programsSubTab === 'musculation' ? (
          <>
            <ProgramsList programs={programs} en={en} authFetch={authFetch} onBack={null} onOpen={openProgram} onNewProgram={() => setNewProgramOpen(true)} onGuide={() => setView('methodology-guide')} />
            <NewProgramModal
              open={newProgramOpen} en={en}
              onClose={closeNewProgramModal}
              onPickBuilder={() => setView('builder')}
              onPickAI={() => setView('ai-generate')}
              onPickAssisted={() => setView('assisted')}
              onPickStandard={() => setView('standard-templates')}
              contextProgramName={contextProgramName}
            />
          </>
        ) : (
          <CardioHub en={en} onBack={null} />
        )}
      </div>
    );
  }

  if (view === 'stats-hub') {
    return (
      <div className="space-y-4" data-testid="stats-hub">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => setView('hub')} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="stats-hub-back-btn">
            <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
          </button>
          <div className="inline-flex rounded-xl bg-zinc-900 border border-white/10 p-1 gap-1">
            <button onClick={() => setStatsSubTab('stats')} data-testid="stats-hub-tab-stats"
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition-colors ${statsSubTab === 'stats' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white' : 'text-zinc-400 hover:text-white'}`}>
              <BarChart3 className="w-4 h-4" /> {L('Statistiques & Records', 'Stats & Records')}
            </button>
            <button onClick={() => setStatsSubTab('records')} data-testid="stats-hub-tab-records"
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-bold transition-colors ${statsSubTab === 'records' ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white' : 'text-zinc-400 hover:text-white'}`}>
              <Trophy className="w-4 h-4" /> {L('Salle des Records', 'Hall of Records')}
            </button>
          </div>
        </div>
        {statsSubTab === 'stats' ? <WorkoutStats en={en} onBack={null} authFetch={authFetch} /> : <RecordsHall en={en} onBack={null} authFetch={authFetch} />}
      </div>
    );
  }

  if (view === 'stats') {
    return <WorkoutStats en={en} onBack={() => setView('hub')} authFetch={authFetch} />;
  }

  if (view === 'records') {
    return <RecordsHall en={en} onBack={() => setView('hub')} authFetch={authFetch} />;
  }

  if (view === 'builder') {
    return (
      <>
        <MyProgramBuilder en={en} onBack={() => { setView('programs-hub'); loadPrograms(); }} onRequestAdjustment={openAdjustmentRequest} />
        <NewProgramModal
          open={newProgramOpen} en={en}
          onClose={closeNewProgramModal}
          onPickBuilder={() => setView('builder')}
          onPickAI={() => setView('ai-generate')}
          onPickAssisted={() => setView('assisted')}
          onPickStandard={() => setView('standard-templates')}
          contextProgramName={contextProgramName}
        />
      </>
    );
  }

  if (view === 'assisted') {
    return <AssistedProgramBuilder en={en} onBack={() => setView('programs-hub')} onCreated={() => { setView('programs-hub'); loadPrograms(); }} />;
  }

  if (view === 'standard-templates') {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <StandardProgramsPicker en={en} authFetch={authFetch} onBack={() => setView('programs-hub')} onAssigned={() => { setView('programs-hub'); loadPrograms(); }} />
      </div>
    );
  }

  if (view === 'ai-generate') {
    return <ClientAIWorkoutGenerator en={en} onBack={() => setView('programs-hub')} onValidated={() => { setView('programs-hub'); loadPrograms(); }} onOpenProgram={(id) => openProgram(id)} />;
  }

  if (view === 'library') {
    return <ExerciseLibrary authFetch={authFetch} en={en} isAdmin={false} onBack={() => setView('hub')} />;
  }

  if (view === 'custom-library') {
    return <CustomExerciseLibrary en={en} onBack={() => setView('hub')} />;
  }

  return (
    <div className="space-y-5" data-testid="workouts-hub">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-black text-white flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-primary" /> {L('Espace Entraînements', 'Training Hub')}
        </h2>
      </div>
      <div className="space-y-3">
        <AppMenuTile
          icon={Dumbbell} gradient="bg-gradient-to-br from-purple-500 to-primary" testId="tile-programs"
          label={L('Mes programmes', 'My programs')} sub={L('Musculation & Cardio · séances, tonnage, AMRAP', 'Strength & Cardio · sessions, tonnage, AMRAP')}
          onClick={openProgramsList}
        />
        <AppMenuTile
          icon={BookOpen} gradient="bg-gradient-to-br from-fuchsia-500 to-pink-600" testId="tile-library"
          label={L("Bibliothèque d'exercices", 'Exercise library')}
          sub={L('~450 exercices classés, images pro', '~450 curated exercises, pro images')}
          onClick={() => setView('library')}
        />
        <AppMenuTile
          icon={Sparkles} gradient="bg-gradient-to-br from-amber-500 to-orange-600" testId="tile-custom-library"
          label={L('Ma bibliothèque créée', 'My created library')}
          sub={L('Créez vos exercices, générez des visuels IA avec vos crédits', 'Create your exercises, generate AI visuals with your credits')}
          onClick={() => setView('custom-library')}
        />
        <div ref={statsTileRef}>
          <AppMenuTile
            icon={BarChart3} gradient="bg-gradient-to-br from-emerald-500 to-teal-600" testId="tile-stats"
            label={L('Statistiques & Records', 'Stats & Records')} sub={L('Badges RM, volume, tendances & palmarès', 'RM badges, volume, trends & hall of fame')}
            onClick={() => setView('stats-hub')}
          />
        </div>
      </div>
      <SmartTooltip
        id="workouts-stats-badges"
        title={{ fr: 'Nouveau : Badges RM', en: 'New: RM Badges' }}
        description={{ fr: 'Découvrez vos records 1RM, 6RM, 10RM et 15RM par exercice, filtrables par groupe musculaire !', en: 'Discover your 1RM, 6RM, 10RM and 15RM records per exercise, filterable by muscle group!' }}
        targetRef={statsTileRef}
        show={!loading}
        position="top"
        en={en}
      />
      <NewProgramModal
        open={newProgramOpen} en={en}
        onClose={closeNewProgramModal}
        onPickBuilder={() => setView('builder')}
        onPickAI={() => setView('ai-generate')}
        onPickAssisted={() => setView('assisted')}
        onPickStandard={() => setView('standard-templates')}
        contextProgramName={contextProgramName}
      />
    </div>
  );
}
